import axios from "axios";

const baseURL =
  process.env.REACT_APP_API_URL ||
  "https://mockq.wahylab.com/api";

const api = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

/* ==========================================================
   REQUEST INTERCEPTOR
   ========================================================== */

api.interceptors.request.use(
  (config) => {
    const publicAuthEndpoints = [
  "/auth/login/",
  "/auth/register/",
  "/auth/beginner/register/",
  "/auth/forgot-password/",
  "/auth/reset-password/",
  "/courses/",
];

    const requestUrl = config.url || "";

    const isPublicAuthRequest =
      publicAuthEndpoints.some((endpoint) =>
        requestUrl.endsWith(endpoint)
      );

    if (!isPublicAuthRequest) {
      const token =
        localStorage.getItem("access_token");

      if (token) {
        config.headers = config.headers || {};

        config.headers.Authorization =
          `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);


/* ==========================================================
   AUTH
   ========================================================== */

export const registerUser = (payload) =>
  api.post("/auth/register/", payload);
export const getExamRegistration = (examId) =>
  api.get(`/exams/${examId}/register/`);

export const findUserByEmail = (email) =>
  api.get(
    `/users?email=${encodeURIComponent(email)}`
  );







export const loginUser = async (email, password) => {
  const response = await api.post("/auth/login/", {
    email: email.trim(),
    password,
  });

  return response.data;
};




export const detectProctoringFrame = (attemptId, frameBlob, isBeginnerAttempt = false) => {
  const formData = new FormData();

  formData.append("frame", frameBlob, "proctor-frame.jpg");

  return api.post(
    isBeginnerAttempt
      ? `/proctoring/beginner-attempts/${attemptId}/detect/`
      : `/proctoring/attempts/${attemptId}/detect/`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
};
export const logoutUser = () => {
  localStorage.removeItem(
    "access_token"
  );

  localStorage.removeItem(
    "refresh_token"
  );
    window.dispatchEvent(new Event("auth:changed"));
};


/* ==========================================================
   PROFILE
   ========================================================== */

export const getProfile = () =>
  api.get("/auth/profile/");

export const updateUserProfile = (
  payload
) =>
  api.patch(
    "/auth/profile/",
    payload
  );

export const changePassword = (
  payload
) =>
  api.post(
    "/auth/change-password/",
    payload
  );


/* ==========================================================
   COURSES / EXAMS
   ========================================================== */

export const getCourses = () =>
  api.get("/courses/");

export const getExams = () =>
  api.get("/exams/");

export const getExam = (
  examId
) =>
  api.get(
    `/exams/${examId}/`
  );

export const getExamQuestions = (
  examId
) =>
  api.get(
    `/exams/${examId}/questions/`
  );


/*
 * LEGACY ONLY
 *
 * The actual Examination page must NOT use this.
 *
 * Questions for a real examination come from:
 *
 * /exams/attempts/<attempt_id>/questions/
 *
 * Keep this function because another old page may still
 * import it.
 */

export const getQuestionsByCourse = (
  courseId
) =>
  api.get(
    `/courses/${courseId}/questions/`
  );


/* ==========================================================
   EXAM REGISTRATION
   ========================================================== */

export const registerForExam = (
  examId
) =>
  api.post(
    `/exams/${examId}/register/`,
    {
      criteria_accepted: true,
    }
  );


/* ==========================================================
   EXAM ATTEMPT
   ========================================================== */

/*
 * Legacy / existing exam start endpoint.
 *
 * Keep it because other existing pages may use it.
 */

export const startExam = (
  examId
) =>
  api.post(
    `/exams/${examId}/start/`
  );


/*
 * REAL EXAM ATTEMPT START
 *
 * Django:
 * POST /api/exams/<exam_id>/attempt/start/
 */

export const startExamAttempt = (
  examId
) =>
  api.post(
    `/exams/${examId}/attempt/start/`
  );


/*
 * Get randomized questions belonging
 * to THIS attempt.
 */

export const getAttemptQuestions = (
  attemptId
) =>
  api.get(
    `/exams/attempts/${attemptId}/questions/`
  );


/*
 * Save an answer.
 *
 * MCQ:
 * {
 *   selected_option_id: 123
 * }
 *
 * Essay:
 * {
 *   essay_answer: "..."
 * }
 */

export const saveAnswer = (
  attemptId,
  questionId,
  payload
) =>
  api.post(
    `/exams/attempts/${attemptId}/questions/${questionId}/answer/`,
    payload
  );


/*
 * Submit the current section.
 */

export const submitExamSection = (
  attemptId,
  sectionId
) =>
  api.post(
    `/exams/attempts/${attemptId}/sections/${sectionId}/submit/`
  );


/*
 * Submit the complete examination.
 *
 * IMPORTANT:
 * React does NOT calculate the result.
 * Django handles grading.
 */

export const submitExam = (
  attemptId
) =>
  api.post(
    `/exams/attempts/${attemptId}/submit/`
  );


/* ==========================================================
   PAYMENT
   ========================================================== */

export const createPaymentOrder = (examId, couponCode = "") =>
  api.post("/payments/create-order/", {
    exam_id: examId,
    ...(couponCode.trim() ? { coupon_code: couponCode.trim() } : {}),
  });

export const validateCoupon = (payload) =>
  api.post("/payments/coupons/validate/", payload);

export const getAdminCoupons = () => api.get("/payments/admin/coupons/");
export const createAdminCoupon = (payload) => api.post("/payments/admin/coupons/", payload);
export const updateAdminCoupon = (id, payload) => api.post(`/payments/admin/coupons/${id}/`, payload);
export const deactivateAdminCoupon = (id) => api.delete(`/payments/admin/coupons/${id}/`);

export const simulatePaymentSuccess = (
  examId
) =>
  api.post(
    "/payments/test-success/",
    {
      exam_id: examId,
    }
  );

export const verifyPayment = ({
  examId,
  razorpayOrderId,
  razorpayPaymentId,
  razorpaySignature,
}) =>
  api.post(
    "/payments/verify/",
    {
      exam_id: examId,
      razorpay_order_id:
        razorpayOrderId,
      razorpay_payment_id:
        razorpayPaymentId,
      razorpay_signature:
        razorpaySignature,
    }
  );

export const getPaymentHistory = () =>
  api.get(
    "/payments/history/"
  );


/*
 * Legacy payment functions.
 *
 * Kept so existing pages do not break.
 */

export const addPayment = (
  payload
) =>
  api.post(
    "/payments/",
    payload
  );

export const createTransaction = (
  payload
) =>
  api.post(
    "/payments/transactions/",
    payload
  );

export const getTransaction = (
  id
) =>
  api.get(
    `/payments/transactions/${id}/`
  );

export const updateTransactionStatus = (
  id,
  statusValue
) =>
  api.patch(
    `/payments/transactions/${id}/`,
    {
      status: statusValue,
    }
  );


/* ==========================================================
   SCHEDULING
   ========================================================== */

export const getExamSchedules = (
  examId
) =>
  api.get(
    `/scheduling/exams/${examId}/slots/`
  );

export const scheduleExam = (
  examId,
  examDate,
  startTime
) =>
  api.post(
    `/scheduling/exams/${examId}/schedule/`,
    {
      exam_date: examDate,
      start_time: startTime,
    }
  );


/* ==========================================================
   VERIFICATION
   ========================================================== */

export const verifyExamCode = (
  examId,
  code
) =>
  api.post(
    `/verification/exams/${examId}/verify/`,
    {
      code,
    }
  );


/* ==========================================================
   PROCTORING
   ========================================================== */

/*
 * START PROCTORING SESSION
 *
 * Django:
 * POST /api/proctoring/attempts/<attempt_id>/session/start/
 */

export const startProctoringSession = (
  attemptId,
  isBeginnerAttempt = false
) =>
  api.post(
    isBeginnerAttempt
      ? `/proctoring/beginner-attempts/${attemptId}/session/start/`
      : `/proctoring/attempts/${attemptId}/session/start/`
  );


/*
 * END PROCTORING SESSION
 *
 * IMPORTANT:
 * This uses /session/ because that is the endpoint
 * used by the current backend integration.
 */

export const endProctoringSession = (
  attemptId,
  isBeginnerAttempt = false
) =>
  api.post(
    isBeginnerAttempt
      ? `/proctoring/beginner-attempts/${attemptId}/session/`
      : `/proctoring/attempts/${attemptId}/session/`
  );


/*
 * RECORD PROCTORING EVENT
 *
 * Django:
 * POST /api/proctoring/attempts/<attempt_id>/events/
 */

export const recordProctoringEvent = (
  attemptId,
  payload,
  isBeginnerAttempt = false
) =>
  api.post(
    isBeginnerAttempt
      ? `/proctoring/beginner-attempts/${attemptId}/events/`
      : `/proctoring/attempts/${attemptId}/events/`,
    payload
  );


/*
 * Alias kept for compatibility with any existing
 * component that currently imports sendProctoringEvent.
 */

export const sendProctoringEvent =
  recordProctoringEvent;


/* ==========================================================
   PASSWORD RESET
   ========================================================== */

/*
 * Request a 6-digit OTP.
 *
 * Django:
 * POST /api/auth/forgot-password/
 */

export const requestPasswordReset = (
  email
) =>
  api.post(
    "/auth/forgot-password/",
    {
      email,
    }
  );


/*
 * Reset password using OTP.
 *
 * Django:
 * POST /api/auth/reset-password/
 */

export const resetPasswordWithCode = (
  email,
  code,
  newPassword
) =>
  api.post(
    "/auth/reset-password/",
    {
      email,
      code,
      new_password: newPassword,
    }
  );


/* ==========================================================
   PURCHASES / SCORES
   ========================================================== */

/*
 * Temporary compatibility functions.
 *
 * These remain so old pages don't break.
 */

export const getPurchasesByUser = () =>
  Promise.resolve({
    data: [],
  });

export const addPurchase = () =>
  Promise.resolve({
    data: null,
  });

export const getScoresByUser = () =>
  Promise.resolve({
    data: [],
  });

export const addScore = (
  payload
) =>
  Promise.resolve({
    data: payload,
  });


/* ==========================================================
   STUDENT RESULTS
   ========================================================== */

export const getStudentResults = () =>
  api.get(
    "/exams/results/"
  );


/* ==========================================================
   DEFAULT API
   ========================================================== */

export const registerBeginnerUser = (payload) =>
  api.post("/auth/beginner/register/", payload);

export default api;